import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Receipt, Plus, X, DeviceMobile, Eye, ArrowCounterClockwise } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { newGroupId, newAmcId, PRESENTATION_MODES, DELIVERY_MODES, DISCOUNT_TYPES } from "../builderStorage";
import { aggregateScopeByProduct } from "../productAggregation";
import { DeliveryIcon } from "../deliveryIcons";

const CURRENCY_OPTIONS = ["INR", "USD", "EUR", "GBP", "AED"];

function formatMoney(n, currency = "INR") {
    if (typeof n !== "number" || isNaN(n)) return "";
    return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 2 }).format(n);
}

/**
 * Section 2 — Commercial Section (Setup Fee + AMC combined).
 *
 * Iter 97.2 (2026-02-26 — REEDOS review feedback):
 *   - "Desktop" delivery mode retired (no longer supported).
 *   - "Hosting" product hides the delivery-modes row entirely (deployment
 *     intent is conveyed by the chosen module — REEDOS Server vs On Premises).
 *   - UNIFIED single layout: the operator enters Setup Fee + Annual AMC at the
 *     module/item level ALWAYS. Group subtotals and the grand total are
 *     auto-computed sums (no manual entry). Presentation mode (Itemized /
 *     Grouped / Consolidated) now changes ONLY the price-visibility in the
 *     PREVIEW output — the proposal structure (rows, products, modules,
 *     groups) is identical across all modes.
 *
 * Pricing model (single source of truth):
 *   Item Setup Fee  →  commercials.itemPrices[scopeRowId]
 *   Item AMC        →  amc.items[*].amount keyed by scopeRowId
 *   Group/Product subtotal = auto-sum of constituent items.
 *   Grand Total = auto-sum of every item.
 *
 * Special Discount still applies to the Setup Fee Grand Total only.
 */
export default function CommercialSection({ scope, commercials, amc, currency, onChange, onAmcChange, onCurrencyChange, onScopeChange }) {
    const setMode = (mode) => onChange({ ...commercials, presentationMode: mode });

    /* Aggregate scope rows into Products */
    const products = useMemo(() => aggregateScopeByProduct(scope), [scope]);

    /* ─────────── Complimentary marker (2026-07-23) ───────────
       Setup Fee and Annual AMC can independently be flagged
       "complimentary" on any scope row. When set:
         - the corresponding effective amount is forced to 0;
         - the amount input is disabled in the UI;
         - the word "Complimentary" is displayed in place of ₹0
           on the approved surfaces (Builder, Preview, Detail, PDF).
       The marker lives inside `builder_state.scope[*]` only —
       nothing new is added to line_items (backend Pydantic drops
       unknown fields). Absent flag defaults to "priced" everywhere,
       so existing ₹0 rows are NEVER inferred as complimentary. */
    const setRowComplimentary = (scopeRowId, component /* "setup" | "amc" | "annual_cost" */, complimentary) => {
        const modeField = { setup: "setup_fee_pricing_mode", amc: "amc_pricing_mode", annual_cost: "annual_cost_pricing_mode" }[component];
        const nextMode = complimentary ? "complimentary" : "priced";
        // Update the scope row's marker (owned by prototype.scope).
        const nextScope = (scope || []).map((sr) =>
            sr.id === scopeRowId ? { ...sr, [modeField]: nextMode } : sr,
        );
        if (typeof onScopeChange === "function") onScopeChange(nextScope);
        // Force the corresponding numeric value to 0 whenever the row
        // is moved into "complimentary". On the reverse transition the
        // amount stays at 0 per REEDOS clarification — the operator
        // types the new value themselves.
        if (complimentary) {
            if (component === "setup") setItemPrice(scopeRowId, 0);
            else if (component === "annual_cost") setRowAnnualCost(scopeRowId, "");
            else setRowAmc(scopeRowId, 0);
        }
    };

    /* ─────────── Setup Fee helpers ─────────── */
    const setItemPrice = (scopeId, price) => {
        onChange({
            ...commercials,
            itemPrices: { ...commercials.itemPrices, [scopeId]: price === "" ? 0 : Number(price) },
        });
    };
    const productSetupSubtotal = (product) =>
        product.scopeRows.reduce((sum, r) => sum + (Number(commercials.itemPrices?.[r.id]) || 0), 0);

    /* ─────────── AMC helpers (iter 92.6 — scope-row level) ─────────── */
    const amcItems = amc?.items || [];
    const amcByScopeRowId = useMemo(() => {
        const m = {};
        amcItems.forEach((a) => { m[a.scopeRowId] = a; });
        return m;
    }, [amcItems]);

    const setRowAmc = (scopeRowId, raw) => {
        const num = raw === "" ? 0 : Number(raw);
        const existing = amcByScopeRowId[scopeRowId];
        let nextItems;
        if (existing) {
            if (num === 0) {
                nextItems = amcItems.filter((a) => a.scopeRowId !== scopeRowId);
            } else {
                nextItems = amcItems.map((a) => (a.scopeRowId === scopeRowId ? { ...a, amount: num } : a));
            }
        } else {
            if (num === 0) return;
            nextItems = [...amcItems, { id: newAmcId(), scopeRowId, amount: num }];
        }
        onAmcChange({ ...amc, items: nextItems });
    };

    const productAmcSubtotal = (product) =>
        product.scopeRows.reduce((s, r) => s + (Number(amcByScopeRowId[r.id]?.amount) || 0), 0);

    /* ─────────── Annual Cost (2026-10) ───────────
       Optional third charge per item (data/content, hosting, server
       management …). Each item carries EITHER Annual AMC OR Annual Cost —
       switching clears the other amount and its Complimentary marker. A
       blank Annual Cost is never treated as Complimentary. */
    const annualCostPrices = commercials.annualCostPrices || {};
    const setRowAnnualCost = (scopeRowId, raw) => {
        const next = { ...annualCostPrices };
        if (raw === "") delete next[scopeRowId];
        else next[scopeRowId] = Number(raw);
        onChange({ ...commercials, annualCostPrices: next });
    };
    const setRowRecurringKind = (scopeRowId, kind) => {
        const nextScope = (scope || []).map((sr) => {
            if (sr.id !== scopeRowId) return sr;
            return kind === "annual_cost"
                ? { ...sr, recurring_kind: "annual_cost", amc_pricing_mode: "priced" }
                : { ...sr, recurring_kind: "amc", annual_cost_pricing_mode: "priced" };
        });
        if (typeof onScopeChange === "function") onScopeChange(nextScope);
        if (kind === "annual_cost") {
            if (amcByScopeRowId[scopeRowId]) onAmcChange({ ...amc, items: amcItems.filter((a) => a.scopeRowId !== scopeRowId) });
        } else if (scopeRowId in annualCostPrices) {
            const next = { ...annualCostPrices };
            delete next[scopeRowId];
            onChange({ ...commercials, annualCostPrices: next });
        }
    };
    const productAnnualSubtotal = (product) =>
        product.scopeRows.reduce((s, r) => s + (r.recurring_kind === "annual_cost" && r.annual_cost_pricing_mode !== "complimentary"
            ? (Number(annualCostPrices[r.id]) || 0) : 0), 0);
    const anyAnnualCost = (scope || []).some((r) => r.recurring_kind === "annual_cost");

    /* ─────────── Group helpers (assignment only; totals are auto) ─────────── */
    const addGroup = () =>
        onChange({
            ...commercials,
            groups: [
                ...(commercials.groups || []),
                { id: newGroupId(), label: "Group " + ((commercials.groups || []).length + 1), productKeys: [] },
            ],
        });
    const updateGroup = (id, patch) =>
        onChange({
            ...commercials,
            groups: (commercials.groups || []).map((g) => (g.id === id ? { ...g, ...patch } : g)),
        });
    const removeGroup = (id) =>
        onChange({
            ...commercials,
            groups: (commercials.groups || []).filter((g) => g.id !== id),
        });
    const toggleGroupProduct = (groupId, productKey, checked) => {
        // Mutual exclusion: a Product lives in at most one group.
        const groups = (commercials.groups || []).map((g) => {
            if (g.id !== groupId) {
                return { ...g, productKeys: (g.productKeys || []).filter((k) => k !== productKey) };
            }
            const current = new Set(g.productKeys || []);
            if (checked) current.add(productKey);
            else current.delete(productKey);
            return { ...g, productKeys: Array.from(current) };
        });
        onChange({ ...commercials, groups });
    };

    const productByKey = useMemo(() => {
        const m = {};
        products.forEach((p) => { m[p.key] = p; });
        return m;
    }, [products]);

    const assignedKeys = useMemo(() => {
        const taken = new Set();
        (commercials.groups || []).forEach((g) => (g.productKeys || []).forEach((k) => taken.add(k)));
        return taken;
    }, [commercials.groups]);

    const unassignedProducts = useMemo(
        () => products.filter((p) => !assignedKeys.has(p.key)),
        [products, assignedKeys],
    );

    /* ─────────── Auto-computed totals ─────────── */
    const groupSetupSubtotal = (g) =>
        (g.productKeys || []).reduce((s, k) => s + (productByKey[k] ? productSetupSubtotal(productByKey[k]) : 0), 0);
    const groupAmcSubtotal = (g) =>
        (g.productKeys || []).reduce((s, k) => s + (productByKey[k] ? productAmcSubtotal(productByKey[k]) : 0), 0);
    const groupAnnualSubtotal = (g) =>
        (g.productKeys || []).reduce((s, k) => s + (productByKey[k] ? productAnnualSubtotal(productByKey[k]) : 0), 0);

    const grandSetupTotal = useMemo(
        () => products.reduce((sum, p) => sum + productSetupSubtotal(p), 0),
        [products, commercials.itemPrices],
    );
    const grandAmcTotal = useMemo(
        () => products.reduce((sum, p) => sum + productAmcSubtotal(p), 0),
        [products, amcByScopeRowId],
    );
    const grandAnnualTotal = products.reduce((sum, p) => sum + productAnnualSubtotal(p), 0);

    /* ─────────── Special Discount (Setup Fee only) ─────────── */
    // UAT 45.6.6 (2026-07-14) — Reason code default is "Relationship" per
    // the operator directive and mirrors the legacy SpecialOfferEditor.
    const discount = commercials.discount || { type: "percent", value: 0, reason: "" };
    const discountAmount = useMemo(() => {
        const v = Number(discount.value) || 0;
        if (v <= 0) return 0;
        if (discount.type === "percent") return Math.min(grandSetupTotal, (grandSetupTotal * v) / 100);
        return Math.min(grandSetupTotal, v);
    }, [discount, grandSetupTotal]);
    const finalCommercialValue = Math.max(0, grandSetupTotal - discountAmount);

    const setDiscountType = (type) => onChange({ ...commercials, discount: { ...discount, type } });
    const setDiscountValue = (raw) => onChange({ ...commercials, discount: { ...discount, value: raw === "" ? 0 : Number(raw) } });
    // Iter 97.20 — Builder.discount expanded with the two fields the backend
    // Special Offer feature requires: a PDF-facing `label` (rendered verbatim
    // on the proposal PDF) and an internal closed-enum `reasonCode` for MIS.
    // The legacy `reason` field is repurposed as internal-only `notes` so it
    // doesn't leak into the client-facing PDF. Backwards-compat: when an old
    // prototype carries only `reason`, treat it as the label (no data loss).
    const setDiscountLabel = (label) => onChange({ ...commercials, discount: { ...discount, label } });
    const setDiscountReasonCode = (reasonCode) => onChange({ ...commercials, discount: { ...discount, reasonCode } });
    const setDiscountNotes = (notes) => onChange({ ...commercials, discount: { ...discount, notes } });
    // UAT 45.6.6 (2026-07-14) — Standardized offer taxonomy setters.
    // ``offerType`` is the code from the backend meta catalog. When the
    // operator selects a canonical (non-CUSTOM) entry, ``label`` is
    // frozen to that entry's display_title (read-only in the UI) and
    // ``description`` is prefilled to the default_description but
    // remains editable for exceptional wording. Selecting CUSTOM unlocks
    // both label and description for free-form entry.
    const setDiscountDescription = (description) =>
        onChange({ ...commercials, discount: { ...discount, description } });
    const setDiscountOfferType = (offerType, autoFill) => {
        // autoFill = { display_title, default_description } | null
        const next = { ...discount, offerType };
        if (autoFill) {
            // For canonical types, force the label to the standard title;
            // for CUSTOM the caller passes null so the operator keeps
            // whatever they had (typically empty until they type).
            next.label = autoFill.display_title;
            next.description = autoFill.default_description || "";
        }
        onChange({ ...commercials, discount: next });
    };

    // UAT 45.6.6 — Fetch the standardized offer_type catalog from the
    // backend once on mount. The frontend does NOT maintain a parallel
    // constant map — this endpoint is the single source of truth.
    const [offerTypes, setOfferTypes] = useState([]);
    const [offerTypesDefaultCode, setOfferTypesDefaultCode] = useState(null);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/billing/proposal/_meta/special-offer/offer-types");
                if (cancelled) return;
                setOfferTypes(data?.offer_types || []);
                setOfferTypesDefaultCode(data?.default_code || null);
            } catch (_err) {
                // Non-fatal — dropdown will be empty; operator can still
                // apply the discount via free-form label if the meta
                // endpoint is unavailable. The `sales:read`-gated endpoint
                // is expected to be reachable for any Builder user.
            }
        })();
        return () => { cancelled = true; };
    }, []);
    const offerTypeByCode = useMemo(() => {
        const m = {};
        offerTypes.forEach((o) => { m[o.code] = o; });
        return m;
    }, [offerTypes]);
    const selectedOfferType = discount.offerType
        ? offerTypeByCode[discount.offerType]
        : (offerTypesDefaultCode ? offerTypeByCode[offerTypesDefaultCode] : null);
    const isCustomOfferType = discount.offerType === "CUSTOM";
    // "Reset to Standard" is offered only when a canonical offer_type is
    // selected AND the operator has manually edited label or description
    // away from the catalog defaults.
    const canResetToStandard = !!selectedOfferType
        && !isCustomOfferType
        && (
            (discount.label || "") !== selectedOfferType.display_title
            || (discount.description || "") !== (selectedOfferType.default_description || "")
        );
    const resetOfferToStandard = () => {
        if (!selectedOfferType) return;
        onChange({
            ...commercials,
            discount: {
                ...discount,
                label: selectedOfferType.display_title,
                description: selectedOfferType.default_description || "",
            },
        });
    };

    // On first mount, if the operator hasn't set an offer_type yet AND
    // the discount has no label / description yet, prefill from the
    // catalog default (RELATIONSHIP_BUILDING) so the fields are usable
    // without an extra click. Also default the reasonCode to
    // "Relationship" — matches the legacy SpecialOfferEditor.
    useEffect(() => {
        if (!offerTypesDefaultCode) return;
        if (discount.offerType) return;
        const def = offerTypeByCode[offerTypesDefaultCode];
        if (!def) return;
        const patch = { ...discount, offerType: def.code };
        if (!discount.label) patch.label = def.display_title;
        if (!discount.description) patch.description = def.default_description || "";
        if (!discount.reasonCode) patch.reasonCode = "Relationship";
        onChange({ ...commercials, discount: patch });
    }, [offerTypesDefaultCode, offerTypeByCode]);

    /* ─────────── Delivery Modes handlers ─────────── */
    const isDeliveryModeChecked = (productKey, modeCode) => {
        const arr = commercials.deliveryModes?.[productKey];
        return Array.isArray(arr) && arr.includes(modeCode);
    };
    const toggleDeliveryMode = (productKey, modeCode) => {
        const current = commercials.deliveryModes?.[productKey] || [];
        const next = current.includes(modeCode) ? current.filter((c) => c !== modeCode) : [...current, modeCode];
        onChange({ ...commercials, deliveryModes: { ...(commercials.deliveryModes || {}), [productKey]: next } });
    };

    /* ─────────── Visibility note per presentation mode ─────────── */
    const visibilityNote = ({
        itemized: "Itemized → Item prices, group/product subtotals, and the grand total are ALL visible in the printed proposal.",
        grouped: "Grouped → Individual item prices are HIDDEN. Group / product subtotals and the grand total remain visible.",
        consolidated: "Consolidated → Item prices AND subtotals are HIDDEN. Only the grand total is visible.",
    })[commercials.presentationMode];

    const annualProps = { prices: annualCostPrices, setPrice: setRowAnnualCost, setKind: setRowRecurringKind, subtotal: productAnnualSubtotal, any: anyAnnualCost };

    /* ─────────── Render ─────────── */
    return (
        <Card className="border-slate-200" data-testid="section-commercial">
            <CardHeader>
                <CardTitle className="flex items-center gap-2"><Receipt weight="duotone" /> Section 2 — Commercial Section (Setup Fee + AMC)</CardTitle>
                <CardDescription>
                    Enter Setup Fee and Annual AMC — or Annual Cost (data/content, hosting, server management) — for each item once. Group / product subtotals and the grand total are auto-computed. Presentation mode below changes ONLY which numbers are visible in the printed proposal — the structure (rows, products, modules, groups) is identical across all modes.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Presentation mode + Currency */}
                <div className="flex items-end justify-between gap-4 flex-wrap">
                    <div className="flex-1">
                        <Label className="text-xs uppercase tracking-wide text-slate-500">Presentation mode (visibility-only)</Label>
                        <RadioGroup
                            value={commercials.presentationMode}
                            onValueChange={setMode}
                            className="mt-2 grid md:grid-cols-3 gap-2"
                            data-testid="presentation-mode-group"
                        >
                            {PRESENTATION_MODES.map((mObj) => (
                                <Label
                                    key={mObj.code}
                                    htmlFor={`mode-${mObj.code}`}
                                    className={`flex items-start gap-2 p-3 rounded-md border cursor-pointer transition ${
                                        commercials.presentationMode === mObj.code ? "bg-indigo-50 border-indigo-300" : "border-slate-200 hover:bg-slate-50"
                                    }`}
                                >
                                    <RadioGroupItem value={mObj.code} id={`mode-${mObj.code}`} data-testid={`mode-${mObj.code}`} />
                                    <div>
                                        <div className="text-sm font-semibold">{mObj.label}</div>
                                        <div className="text-xs text-slate-500">{mObj.description}</div>
                                    </div>
                                </Label>
                            ))}
                        </RadioGroup>
                    </div>
                    <div className="w-32">
                        <Label className="text-xs uppercase tracking-wide text-slate-500">Currency</Label>
                        <Select value={currency} onValueChange={onCurrencyChange}>
                            <SelectTrigger className="mt-2" data-testid="currency-select"><SelectValue /></SelectTrigger>
                            <SelectContent>{CURRENCY_OPTIONS.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}</SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Visibility note */}
                <div className="flex items-start gap-2 text-xs text-indigo-900 bg-indigo-50/60 border border-indigo-200 rounded p-3" data-testid="visibility-note">
                    <Eye className="flex-shrink-0 mt-0.5" weight="duotone" />
                    <div>{visibilityNote}</div>
                </div>

                {products.length === 0 && (
                    <div className="text-sm text-slate-500 italic p-3 bg-amber-50 border border-amber-200 rounded">
                        Add products in Section 1 to begin commercial structuring.
                    </div>
                )}

                {/* ─────────── Delivery Modes per Product (hidden for products flagged noDeliveryModes) ─────────── */}
                {products.length > 0 && products.some((p) => !p.noDeliveryModes) && (
                    <div className="space-y-2" data-testid="delivery-modes-block">
                        <div className="flex items-center gap-2">
                            <DeviceMobile className="text-slate-600" weight="duotone" />
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Delivery modes per product</div>
                        </div>
                        <div className="border border-slate-200 rounded overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-xs">
                                    <tr><th className="text-left px-3 py-2 font-medium w-1/3">Product</th><th className="text-left px-3 py-2 font-medium">Modes</th></tr>
                                </thead>
                                <tbody>
                                    {products.filter((p) => !p.noDeliveryModes).map((p) => (
                                        <tr key={p.key} className="border-t border-slate-200" data-testid={`delivery-row-${p.key}`}>
                                            <td className="px-3 py-2">
                                                <div className="font-medium text-slate-800">{p.label}</div>
                                                {p.description && <div className="text-[10px] text-slate-500">{p.description}</div>}
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className="flex flex-wrap gap-1.5">
                                                    {DELIVERY_MODES.map((dm) => {
                                                        const checked = isDeliveryModeChecked(p.key, dm.code);
                                                        return (
                                                            <button key={dm.code} type="button" onClick={() => toggleDeliveryMode(p.key, dm.code)}
                                                                className={`text-xs px-2.5 py-1 rounded-full border transition flex items-center gap-1.5 ${checked ? "bg-emerald-50 border-emerald-300 text-emerald-900 font-medium" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}
                                                                data-testid={`delivery-chip-${p.key}-${dm.code}`}>
                                                                <DeliveryIcon iconName={dm.iconName} label={dm.label} size={12} />
                                                                {dm.label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {products.some((p) => p.noDeliveryModes) && (
                            <div className="text-[11px] text-slate-500 italic">
                                Note: Hosting does not use application delivery modes — its delivery is implicit in the chosen module (REEDOS Server or On Premises).
                            </div>
                        )}
                    </div>
                )}

                {/* ─────────── Unified Commercial Structure ─────────── */}
                {products.length > 0 && (
                    <div className="space-y-4" data-testid="commercial-structure-block">
                        <div className="flex items-center justify-between">
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Commercial structure (enter prices once at the item level)</div>
                            <Button size="sm" variant="outline" onClick={addGroup} data-testid="add-group-btn"><Plus className="mr-1" /> Add group</Button>
                        </div>

                        {/* Operator-named groups */}
                        {(commercials.groups || []).map((g) => {
                            const assignedProducts = (g.productKeys || []).map((k) => productByKey[k]).filter(Boolean);
                            const gSetup = groupSetupSubtotal(g);
                            const gAmc = groupAmcSubtotal(g);
                            const gAnnual = groupAnnualSubtotal(g);
                            return (
                                <div key={g.id} className="border border-slate-300 rounded overflow-hidden" data-testid={`group-row-${g.id}`}>
                                    <div className="bg-indigo-50/60 border-b border-slate-200 px-3 py-2 flex items-center justify-between gap-2">
                                        <div className="flex-1 flex items-center gap-2">
                                            <Input value={g.label} onChange={(e) => updateGroup(g.id, { label: e.target.value })} placeholder="Group label (e.g. Digital Wealth Platform)" className="max-w-md bg-white" data-testid={`group-label-${g.id}`} />
                                        </div>
                                        <Button size="sm" variant="ghost" onClick={() => removeGroup(g.id)} data-testid={`group-remove-${g.id}`}><X /></Button>
                                    </div>
                                    <div className="px-3 py-2 border-b border-slate-100">
                                        <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1.5">Assign products to this group</div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {products.map((p) => {
                                                const isIn = (g.productKeys || []).includes(p.key);
                                                return (
                                                    <button key={p.key} type="button" onClick={() => toggleGroupProduct(g.id, p.key, !isIn)}
                                                        className={`text-xs px-2.5 py-1 rounded-full border transition ${isIn ? "bg-indigo-100 border-indigo-300 text-indigo-900 font-medium" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}
                                                        data-testid={`group-assign-${g.id}-${p.key}`}>{p.label}</button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    {assignedProducts.length === 0 ? (
                                        <div className="px-3 py-3 text-xs text-slate-500 italic">No products assigned yet.</div>
                                    ) : (
                                        assignedProducts.map((p) => (
                                            <ProductPriceTable
                                                key={p.key}
                                                product={p}
                                                commercials={commercials}
                                                amcByScopeRowId={amcByScopeRowId}
                                                setItemPrice={setItemPrice}
                                                setRowAmc={setRowAmc}
                                                setRowComplimentary={setRowComplimentary}
                                                productSetupSubtotal={productSetupSubtotal}
                                                productAmcSubtotal={productAmcSubtotal}
                                                annual={annualProps}
                                                deliveryModeCodes={commercials.deliveryModes?.[p.key] || []}
                                                currency={currency}
                                                testidPrefix={`group-${g.id}-product-${p.key}`}
                                            />
                                        ))
                                    )}
                                    <div className="bg-slate-100 border-t border-slate-300 px-3 py-2 flex justify-between items-center font-semibold text-sm">
                                        <span>{g.label || "Group"} Subtotal</span>
                                        <span className="flex gap-6">
                                            <span data-testid={`group-setup-subtotal-${g.id}`}>{formatMoney(gSetup, currency)}</span>
                                            <span className="text-emerald-900" data-testid={`group-amc-subtotal-${g.id}`}>{formatMoney(gAmc, currency)}</span>
                                            {anyAnnualCost && <span className="text-sky-900" data-testid={`group-annual-cost-subtotal-${g.id}`}>{formatMoney(gAnnual, currency)}</span>}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}

                        {/* Unassigned products (auto-displayed as their own product rows) */}
                        {unassignedProducts.length > 0 && (
                            <div className="space-y-3" data-testid="unassigned-products-block">
                                {(commercials.groups || []).length > 0 && (
                                    <div className="text-[11px] uppercase tracking-wide text-slate-500">Products not in any group (auto-displayed)</div>
                                )}
                                {unassignedProducts.map((p) => (
                                    <ProductPriceTable
                                        key={p.key}
                                        product={p}
                                        commercials={commercials}
                                        amcByScopeRowId={amcByScopeRowId}
                                        setItemPrice={setItemPrice}
                                        setRowAmc={setRowAmc}
                                        setRowComplimentary={setRowComplimentary}
                                        productSetupSubtotal={productSetupSubtotal}
                                        productAmcSubtotal={productAmcSubtotal}
                                        annual={annualProps}
                                        deliveryModeCodes={commercials.deliveryModes?.[p.key] || []}
                                        currency={currency}
                                        testidPrefix={`unassigned-product-${p.key}`}
                                        showProductCard
                                    />
                                ))}
                            </div>
                        )}

                        {/* Grand Total */}
                        <div className={`grid gap-3 pt-2 ${anyAnnualCost ? "grid-cols-3" : "grid-cols-2"}`}>
                            <div className="flex justify-between items-center p-3 bg-slate-900 text-white border border-slate-900 rounded font-semibold text-sm">
                                <span>Setup Fee Grand Total</span>
                                <span data-testid="grand-setup-total">{formatMoney(grandSetupTotal, currency)}</span>
                            </div>
                            <div className="flex justify-between items-center p-3 bg-emerald-700 text-white border border-emerald-700 rounded font-semibold text-sm">
                                <span>Annual AMC Grand Total</span>
                                <span data-testid="grand-amc-total">{formatMoney(grandAmcTotal, currency)}</span>
                            </div>
                            {anyAnnualCost && (
                                <div className="flex justify-between items-center p-3 bg-sky-800 text-white border border-sky-800 rounded font-semibold text-sm">
                                    <span>Annual Cost Grand Total</span>
                                    <span data-testid="grand-annual-cost-total">{formatMoney(grandAnnualTotal, currency)}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ─────────── Special Discount ─────────── */}
                {products.length > 0 && (
                    <div className="border border-slate-200 rounded p-4 space-y-3 bg-slate-50/40" data-testid="discount-block">
                        <div className="flex items-baseline justify-between flex-wrap gap-2">
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Special discount (applies to Setup Fee Grand Total only)</div>
                            <div className="text-[10px] text-slate-500">
                                Iter 97.20 — Save&nbsp;/&nbsp;Apply persists this as the proposal&apos;s Special Offer (rendered on PDF).
                            </div>
                        </div>
                        <div className="grid gap-3 md:grid-cols-[180px_180px]">
                            <div>
                                <Label className="text-xs text-slate-600">Discount type</Label>
                                <Select value={discount.type} onValueChange={setDiscountType}>
                                    <SelectTrigger className="mt-1" data-testid="discount-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>{DISCOUNT_TYPES.map((d) => <SelectItem key={d.code} value={d.code}>{d.label}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs text-slate-600">Value {discount.type === "percent" ? "(%)" : `(${currency})`}</Label>
                                <Input type="number" value={discount.value ?? ""} onChange={(e) => setDiscountValue(e.target.value)} placeholder="0" className="mt-1 text-right" data-testid="discount-value" />
                            </div>
                        </div>

                        {/* UAT 45.6.6 · Offer Type dropdown (standardised commercial taxonomy).
                            10 canonical entries + CUSTOM. Auto-populates the Label + Description
                            (both editable for CUSTOM; label is locked for canonical types). */}
                        <div>
                            <Label className="text-xs text-slate-600">
                                Offer type <span className="text-rose-600">*</span>{" "}
                                <span className="font-normal text-slate-500">— standard commercial category (auto-fills label & description)</span>
                            </Label>
                            <Select
                                value={discount.offerType || offerTypesDefaultCode || ""}
                                onValueChange={(code) => {
                                    const entry = offerTypeByCode[code];
                                    // CUSTOM → don't autofill; canonical → autofill label + description
                                    setDiscountOfferType(code, entry && entry.code !== "CUSTOM" ? entry : null);
                                    if (entry && entry.code === "CUSTOM") {
                                        // For CUSTOM, blank out the label so the operator types their own
                                        // (unless they've already typed something).
                                        if ((discount.label || "") && offerTypes.some((o) => o.display_title === discount.label)) {
                                            // Only reset if the current label is a leftover canonical title
                                            onChange({ ...commercials, discount: { ...discount, offerType: "CUSTOM", label: "", description: "" } });
                                        }
                                    }
                                }}
                            >
                                <SelectTrigger className="mt-1" data-testid="discount-offer-type">
                                    <SelectValue placeholder="Select offer type…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {offerTypes.map((o) => (
                                        <SelectItem key={o.code} value={o.code} data-testid={`discount-offer-type-opt-${o.code}`}>
                                            {o.display_title}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label className="text-xs text-slate-600">
                                Offer label <span className="text-rose-600">*</span>{" "}
                                <span className="font-normal text-slate-500">
                                    — shown on PDF{" "}
                                    {!isCustomOfferType && "· locked to the standard title (choose Custom Discount to override)"}
                                </span>
                            </Label>
                            <Input
                                value={discount.label ?? discount.reason ?? ""}
                                onChange={(e) => isCustomOfferType && setDiscountLabel(e.target.value)}
                                readOnly={!isCustomOfferType}
                                placeholder={isCustomOfferType ? "Enter a custom offer title" : "Auto-filled from the selected offer type"}
                                className={`mt-1 ${!isCustomOfferType ? "bg-slate-100 text-slate-700 cursor-not-allowed" : ""}`}
                                maxLength={120}
                                data-testid="discount-label"
                            />
                        </div>

                        <div>
                            <div className="flex items-baseline justify-between gap-2">
                                <Label className="text-xs text-slate-600">
                                    Customer-facing description{" "}
                                    <span className="font-normal text-slate-500">— shown on PDF · editable for exceptional wording</span>
                                </Label>
                                {canResetToStandard && (
                                    <button
                                        type="button"
                                        onClick={resetOfferToStandard}
                                        className="text-[11px] text-indigo-600 hover:text-indigo-800 hover:underline flex items-center gap-1"
                                        data-testid="discount-reset-to-standard"
                                        title="Restore the label and description to the standard text for the selected offer type"
                                    >
                                        <ArrowCounterClockwise size={11} /> Reset to standard
                                    </button>
                                )}
                            </div>
                            <Textarea
                                value={discount.description ?? ""}
                                onChange={(e) => setDiscountDescription(e.target.value)}
                                placeholder={isCustomOfferType
                                    ? "Describe the discount rationale for the client"
                                    : "Auto-filled from the selected offer type — edit only for exceptional wording"}
                                className="mt-1"
                                rows={3}
                                maxLength={1000}
                                data-testid="discount-description"
                            />
                        </div>
                        <div className="grid gap-3 md:grid-cols-[180px_1fr]">
                            <div>
                                <Label className="text-xs text-slate-600">Reason code <span className="text-rose-600">*</span> <span className="font-normal text-slate-500">— internal</span></Label>
                                <Select value={discount.reasonCode || ""} onValueChange={setDiscountReasonCode}>
                                    <SelectTrigger className="mt-1" data-testid="discount-reason-code">
                                        <SelectValue placeholder="Select…" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {[
                                            "Strategic", "Relationship", "Competitive", "Promotional", "Existing-Client",
                                        ].map((c) => (
                                            <SelectItem key={c} value={c}>{c}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs text-slate-600">Internal notes <span className="font-normal text-slate-500">(optional · NOT shown on PDF)</span></Label>
                                <Textarea
                                    value={discount.notes ?? ""}
                                    onChange={(e) => setDiscountNotes(e.target.value)}
                                    placeholder="Operator bookkeeping — e.g. approved by Mr. X on 5-Feb-2026"
                                    className="mt-1"
                                    rows={2}
                                    data-testid="discount-notes"
                                />
                            </div>
                        </div>
                        <div className="border-t border-slate-200 pt-3 text-sm space-y-1" data-testid="discount-preview">
                            <div className="flex justify-between"><span className="text-slate-600">Setup Fee Grand Total</span><span className="font-medium" data-testid="discount-preview-subtotal">{formatMoney(grandSetupTotal, currency)}</span></div>
                            <div className="flex justify-between text-amber-900"><span>Special Discount{discount.value > 0 && discount.type === "percent" && ` (${discount.value}%)`}</span><span data-testid="discount-preview-amount">− {formatMoney(discountAmount, currency)}</span></div>
                            <div className="flex justify-between border-t border-slate-300 pt-1 font-semibold text-slate-900"><span>Final Setup Fee</span><span data-testid="discount-preview-final">{formatMoney(finalCommercialValue, currency)}</span></div>
                            <div className="flex justify-between text-slate-600 pt-2"><span>Annual AMC (unaffected by discount)</span><span data-testid="discount-preview-amc">{formatMoney(grandAmcTotal, currency)}</span></div>
                            {anyAnnualCost && <div className="flex justify-between text-slate-600"><span>Annual Cost (unaffected by discount)</span><span data-testid="discount-preview-annual-cost">{formatMoney(grandAnnualTotal, currency)}</span></div>}
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

/* ─────────────────────────────────────────────────────────────────────────
 * ProductPriceTable — renders a single Product card with:
 *   - Product header (label + description + delivery icons)
 *   - Module rows (or single row for standalone) with Setup Fee + AMC inputs
 *   - Product subtotal row (only when hasModules)
 *
 * Used inside Groups (assigned products) AND inside the unassigned section.
 * ───────────────────────────────────────────────────────────────────────── */
function ProductPriceTable({
    product, commercials, amcByScopeRowId, setItemPrice, setRowAmc, setRowComplimentary,
    productSetupSubtotal, productAmcSubtotal, annual, deliveryModeCodes, currency,
    testidPrefix, showProductCard = false,
}) {
    const setupSub = productSetupSubtotal(product);
    const amcSub = productAmcSubtotal(product);
    const annualSub = annual.subtotal(product);
    const hideDelivery = !!product.noDeliveryModes;
    const Wrapper = showProductCard ? "div" : "div";
    const wrapperClass = showProductCard
        ? "border border-slate-200 rounded overflow-hidden"
        : "border-t border-slate-100";

    return (
        <Wrapper className={wrapperClass} data-testid={testidPrefix}>
            <div className="bg-slate-50/60 px-3 py-2 flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 text-sm flex items-center gap-2 flex-wrap">
                        {product.label}
                        {product.source === "custom" && <span className="text-[10px] uppercase tracking-wide text-indigo-700 font-semibold">Custom</span>}
                        {!hideDelivery && deliveryModeCodes.length > 0 && (
                            <span className="inline-flex items-center gap-1 text-slate-500" data-testid={`${testidPrefix}-delivery-icons`}>
                                {deliveryModeCodes.map((code) => {
                                    const mode = DELIVERY_MODES.find((m) => m.code === code);
                                    if (!mode) return null;
                                    return <DeliveryIcon key={code} iconName={mode.iconName} label={mode.label} size={14} />;
                                })}
                            </span>
                        )}
                    </div>
                    {product.description && (
                        <div className="text-[11px] text-slate-500 italic mt-0.5">{product.description}</div>
                    )}
                </div>
            </div>
            <table className="w-full text-sm">
                <thead className="text-xs bg-slate-50/40">
                    <tr>
                        <th className="text-left px-3 py-1.5 font-medium">{product.hasModules ? "Module" : "Product"}</th>
                        <th className="text-right px-3 py-1.5 font-medium w-72">Setup Fee</th>
                        <th className="text-right px-3 py-1.5 font-medium w-80">{annual.any ? "Annual AMC / Annual Cost" : "Annual AMC"}</th>
                    </tr>
                </thead>
                <tbody>
                    {product.scopeRows.map((r) => {
                        // Complimentary marker (2026-07-23): read directly
                        // from the source scope row. Absent → "priced".
                        const setupComp = r.setup_fee_pricing_mode === "complimentary";
                        const amcComp = r.amc_pricing_mode === "complimentary";
                        return (
                        <tr key={r.id} className="border-t border-slate-100" data-testid={`item-row-${r.id}`}>
                            <td className="px-3 py-2 pl-6 text-slate-700">{r.productName}</td>
                            <td className="px-3 py-2">
                                <div className="flex items-center justify-end gap-2">
                                    {setupComp ? (
                                        <span
                                            className="flex-1 text-right text-indigo-700 font-medium italic pr-1"
                                            data-testid={`item-setup-complimentary-label-${r.id}`}
                                        >
                                            Complimentary
                                        </span>
                                    ) : (
                                        <Input
                                            type="number"
                                            value={commercials.itemPrices?.[r.id] ?? ""}
                                            onChange={(e) => setItemPrice(r.id, e.target.value)}
                                            placeholder="0"
                                            className="text-right flex-1"
                                            data-testid={`item-setup-${r.id}`}
                                        />
                                    )}
                                    <label
                                        className="inline-flex items-center gap-1 text-[11px] text-slate-600 select-none cursor-pointer whitespace-nowrap"
                                        title="Mark Setup Fee as Complimentary (₹0, no charge)"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={setupComp}
                                            onChange={(e) => setRowComplimentary(r.id, "setup", e.target.checked)}
                                            className="h-3.5 w-3.5 cursor-pointer"
                                            data-testid={`item-setup-complimentary-toggle-${r.id}`}
                                        />
                                        Complimentary
                                    </label>
                                </div>
                            </td>
                            <td className="px-3 py-2">
                                <RecurringKindSwitch row={r} onPick={(k) => annual.setKind(r.id, k)} />
                                {r.recurring_kind === "annual_cost" ? (
                                    <AnnualCostInput row={r} price={annual.prices[r.id]} onPrice={(v) => annual.setPrice(r.id, v)}
                                        onComplimentary={(c) => setRowComplimentary(r.id, "annual_cost", c)} />
                                ) : (
                                <div className="flex items-center justify-end gap-2">
                                    {amcComp ? (
                                        <span
                                            className="flex-1 text-right text-indigo-700 font-medium italic pr-1"
                                            data-testid={`item-amc-complimentary-label-${r.id}`}
                                        >
                                            Complimentary
                                        </span>
                                    ) : (
                                        <Input
                                            type="number"
                                            value={amcByScopeRowId[r.id]?.amount ?? ""}
                                            onChange={(e) => setRowAmc(r.id, e.target.value)}
                                            placeholder="0"
                                            className="text-right flex-1"
                                            data-testid={`item-amc-${r.id}`}
                                        />
                                    )}
                                    <label
                                        className="inline-flex items-center gap-1 text-[11px] text-slate-600 select-none cursor-pointer whitespace-nowrap"
                                        title="Mark Annual AMC as Complimentary (₹0, no charge)"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={amcComp}
                                            onChange={(e) => setRowComplimentary(r.id, "amc", e.target.checked)}
                                            className="h-3.5 w-3.5 cursor-pointer"
                                            data-testid={`item-amc-complimentary-toggle-${r.id}`}
                                        />
                                        Complimentary
                                    </label>
                                </div>
                                )}
                            </td>
                        </tr>
                        );
                    })}
                    {product.hasModules && (
                        <tr className="border-t border-slate-300 bg-slate-50 font-semibold">
                            <td className="px-3 py-2 pl-6">{product.label} Subtotal</td>
                            <td className="px-3 py-2 text-right" data-testid={`product-setup-subtotal-${product.key}`}>{formatMoney(setupSub, currency)}</td>
                            <td className="px-3 py-2 text-right text-emerald-900" data-testid={`product-amc-subtotal-${product.key}`}>
                                {formatMoney(amcSub, currency)}
                                {annual.any && <div className="text-sky-900 text-xs" data-testid={`product-annual-cost-subtotal-${product.key}`}>Annual Cost {formatMoney(annualSub, currency)}</div>}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </Wrapper>
    );
}

function RecurringKindSwitch({ row, onPick }) {
    const kind = row.recurring_kind === "annual_cost" ? "annual_cost" : "amc";
    const btn = (k, label) => (
        <button
            type="button"
            onClick={() => kind !== k && onPick(k)}
            className={`px-2 py-0.5 text-[10px] font-semibold transition-colors ${kind === k ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}
            data-testid={`item-recurring-${k === "amc" ? "amc" : "annual-cost"}-${row.id}`}
            aria-pressed={kind === k}
        >
            {label}
        </button>
    );
    return (
        <div className="flex justify-end mb-1">
            <div className="inline-flex rounded-full border border-slate-300 overflow-hidden" role="group" title="An item carries either Annual AMC or Annual Cost">
                {btn("amc", "AMC")}
                {btn("annual_cost", "Annual Cost")}
            </div>
        </div>
    );
}

function AnnualCostInput({ row, price, onPrice, onComplimentary }) {
    const comp = row.annual_cost_pricing_mode === "complimentary";
    const missing = !comp && !(Number(price) > 0);
    return (
        <div className="flex items-center justify-end gap-2">
            {comp ? (
                <span className="flex-1 text-right text-indigo-700 font-medium italic pr-1" data-testid={`item-annual-cost-complimentary-label-${row.id}`}>
                    Complimentary
                </span>
            ) : (
                <Input
                    type="number"
                    value={price ?? ""}
                    onChange={(e) => onPrice(e.target.value)}
                    placeholder="Annual Cost"
                    className={`text-right flex-1 ${missing ? "border-amber-400" : ""}`}
                    data-testid={`item-annual-cost-${row.id}`}
                />
            )}
            <label className="inline-flex items-center gap-1 text-[11px] text-slate-600 select-none cursor-pointer whitespace-nowrap" title="Mark Annual Cost as Complimentary (₹0, no charge)">
                <input
                    type="checkbox"
                    checked={comp}
                    onChange={(e) => onComplimentary(e.target.checked)}
                    className="h-3.5 w-3.5 cursor-pointer"
                    data-testid={`item-annual-cost-complimentary-toggle-${row.id}`}
                />
                Complimentary
            </label>
        </div>
    );
}
